import { authService, type LoginRequest, type RegisterRequest } from '@/services/auth'
import { showToast } from './toast'

export class AuthModal {
  private modal: HTMLElement | null = null
  private isLoginMode: boolean = true
  private captchaToken: string | null = null
  private turnstileWidgetId: string | null = null

  constructor() {
    // onAuthSuccess callback no longer needed since we reload the page
  }

  show(isLoginMode: boolean = true): void {
    this.isLoginMode = isLoginMode
    this.createModal()
    this.attachEventListeners()
  }

  hide(): void {
    if (this.modal) {
      this.modal.classList.remove('modal-open')
      
      // Wait for animation to complete
      setTimeout(() => {
        this.modal?.remove()
        this.modal = null
      }, 300)
    }
  }

  private createModal(): void {
    // Remove existing modal if any
    this.hide()

    this.modal = document.createElement('div')
    this.modal.className = 'modal-backdrop'
    this.modal.innerHTML = `
      <div class="modal-container modal-sm">
        <div class="modal-header">
          <h2 class="modal-title">
            ${this.isLoginMode ? 'Sign In' : 'Create Account'}
          </h2>
          <button id="auth-modal-close" class="modal-close-btn" aria-label="Close modal">&times;</button>
        </div>
        <div class="modal-body">

          <!-- Form -->
          <form id="auth-form" class="space-y-4">
            <div class="form-group">
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                class="input"
                placeholder="Enter your email"
              />
            </div>

            <div class="form-group">
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                minlength="8"
                class="input"
                placeholder="Enter your password"
              />
            </div>

            ${!this.isLoginMode ? `
              <div class="form-group">
                <label for="display_name" class="block text-sm font-medium text-gray-700 mb-1">
                  Display Name (Optional)
                </label>
                <input
                  type="text"
                  id="display_name"
                  name="display_name"
                  class="input"
                  placeholder="Enter your display name"
                />
              </div>
              <div>
                <div id="turnstile-container" class="mt-2"></div>
                <div id="captcha-error" class="text-red-600 text-sm mt-2 text-center hidden"></div>
              </div>
            ` : ''}

            <button
              type="submit"
              id="auth-submit"
              class="btn btn-primary w-full"
            >
              ${this.isLoginMode ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <!-- Toggle Mode -->
          <div class="mt-6 text-center">
            <p class="text-sm text-gray-600">
              ${this.isLoginMode ? "Don't have an account?" : "Already have an account?"}
              <button
                id="auth-toggle-mode"
                class="text-blue-600 hover:text-blue-800 font-medium ml-1"
              >
                ${this.isLoginMode ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>

          <!-- Loading State -->
          <div id="auth-loading" class="hidden mt-4 text-center">
            <div class="spinner"></div>
            <p class="mt-2 text-sm text-gray-600">Processing...</p>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(this.modal)

    // Add modal-open class for animation
    requestAnimationFrame(() => {
      this.modal?.classList.add('modal-open')
    })

    // Load Turnstile script when in sign-up mode
    if (!this.isLoginMode) {
      this.loadTurnstile()
    }
  }

  private attachEventListeners(): void {
    if (!this.modal) return

    // Close modal
    const closeBtn = this.modal.querySelector('#auth-modal-close')
    closeBtn?.addEventListener('click', () => this.hide())

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide()
      }
    })

    // Toggle mode
    const toggleBtn = this.modal.querySelector('#auth-toggle-mode')
    toggleBtn?.addEventListener('click', () => {
      this.isLoginMode = !this.isLoginMode
      this.createModal()
      this.attachEventListeners()
    })

    // Form submission
    const form = this.modal.querySelector('#auth-form') as HTMLFormElement
    form?.addEventListener('submit', (e) => {
      e.preventDefault()
      this.handleSubmit()
    })
  }

  private loadTurnstile(): void {
    // Avoid double loading
    if (document.querySelector('script[data-turnstile]')) {
      this.renderTurnstileWidget()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    // IMPORTANT: Don't use async/defer when using turnstile.render() directly
    script.async = false
    script.setAttribute('data-turnstile', 'true')
    script.onload = () => {
      // Small delay to ensure Turnstile is fully initialized
      setTimeout(() => this.renderTurnstileWidget(), 100)
    }
    document.head.appendChild(script)
  }

  private renderTurnstileWidget(): void {
    const container = this.modal?.querySelector('#turnstile-container') as HTMLElement | null
    if (!container) return
    container.innerHTML = ''

    // Site key from Vite env (compile-time). Do not fallback to placeholder.
    const siteKey = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY
    if (!siteKey) {
      try {
        // Optional runtime override for quick testing without rebuild
        const runtimeKey = (window as any).VITE_TURNSTILE_SITE_KEY
        if (runtimeKey) {
          // @ts-ignore
          this.turnstileWidgetId = (window as any).turnstile?.render(container, {
            sitekey: runtimeKey,
            theme: 'light',
            appearance: 'always',
            callback: (token: string) => { this.captchaToken = token },
            'error-callback': () => { this.captchaToken = null },
            'expired-callback': () => { this.captchaToken = null }
          })
          return
        }
      } catch {}
      // If still no key, inform user and skip rendering
      showToast('Missing VITE_TURNSTILE_SITE_KEY in frontend build', 'error')
      return
    }

    // @ts-ignore - global turnstile injected by script
    if (typeof (window as any).turnstile !== 'undefined') {
      // @ts-ignore
      this.turnstileWidgetId = (window as any).turnstile.render(container, {
        sitekey: siteKey,
        theme: 'light',
        appearance: 'always',
        callback: (token: string) => {
          this.captchaToken = token
          // Clear any existing error message when CAPTCHA is completed
          const captchaErrorEl = this.modal?.querySelector('#captcha-error') as HTMLElement
          if (captchaErrorEl) {
            captchaErrorEl.classList.add('hidden')
          }
        },
        'error-callback': () => {
          this.captchaToken = null
        },
        'expired-callback': () => {
          this.captchaToken = null
        }
      })
    }
  }

  private async handleSubmit(): Promise<void> {
    if (!this.modal) return

    const form = this.modal.querySelector('#auth-form') as HTMLFormElement
    const formData = new FormData(form)
    
    const email = formData.get('email') as string
    const password = formData.get('password') as string
    const displayName = formData.get('display_name') as string

    if (!email || !password) {
      showToast('Please fill in all required fields', 'error')
      return
    }

    if (password.length < 8) {
      showToast('Password must be at least 8 characters long', 'error')
      return
    }

    this.setLoading(true)

    try {
      if (this.isLoginMode) {
        // Login
        const loginData: LoginRequest = { email, password }
        const response = await authService.login(loginData)
        const welcomeName = response.user?.display_name || response.user?.email || 'user'
        showToast(`Welcome back, ${welcomeName}!`, 'success')
        this.hide()
        
        // Reload the page to refresh the application state
        setTimeout(() => {
          window.location.reload()
        }, 1000) // Small delay to show the toast
      } else {
        // Register
        // Ensure CAPTCHA token exists
        if (!this.captchaToken) {
          try {
            // @ts-ignore
            const existing = this.turnstileWidgetId ? (window as any).turnstile?.getResponse(this.turnstileWidgetId) : null
            if (existing) {
              this.captchaToken = existing
            }
          } catch {}
        }
        if (!this.captchaToken) {
          // Show inline error message instead of toast
          const captchaErrorEl = this.modal?.querySelector('#captcha-error') as HTMLElement
          if (captchaErrorEl) {
            captchaErrorEl.textContent = 'Please complete the CAPTCHA'
            captchaErrorEl.classList.remove('hidden')
          }
          this.setLoading(false)
          return
        }
        const registerData: RegisterRequest = { 
          email, 
          password, 
          display_name: displayName || undefined,
          captchaToken: this.captchaToken || undefined
        }
        try {
          // Call Better Auth sign-up directly to avoid the user object requirement
          await authService.signUp({
            email: registerData.email,
            password: registerData.password,
            name: registerData.display_name,
            captchaToken: registerData.captchaToken
          })
          
          // Registration successful - show verification message
          const welcomeName = displayName || email.split('@')[0]
          this.showEmailVerificationMessage(email, welcomeName)
        } catch (registerError: any) {
          console.error('Registration error:', registerError)
          
          // Check if this is a network/server error vs expected behavior
          if (registerError.response?.status >= 500) {
            // Server error - show error message
            showToast('Registration failed due to server error. Please try again.', 'error')
            return
          } else if (registerError.response?.status === 400) {
            // Client error - show the error message
            const errorMessage = registerError.response?.data?.error || registerError.message || 'Registration failed'
            showToast(errorMessage, 'error')
            return
          } else {
            // Other errors - still show verification message as registration might have succeeded
            console.log('Registration may have succeeded despite error, showing verification message')
            const welcomeName = displayName || email.split('@')[0]
            this.showEmailVerificationMessage(email, welcomeName)
          }
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error)
      
      let errorMessage = 'An error occurred. Please try again.'
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }
      
      // Special handling for email verification errors
      if (errorMessage.includes('verify your email')) {
        showToast(errorMessage, 'error')
        // Show email verification modal for unverified users
        this.showEmailVerificationMessage(email, email.split('@')[0])
        return
      }
      
      showToast(errorMessage, 'error')
      // Close the modal when login fails so user can try again
      this.hide()
    } finally {
      this.setLoading(false)
    }
  }

  private setLoading(loading: boolean): void {
    if (!this.modal) return

    const submitBtn = this.modal.querySelector('#auth-submit') as HTMLButtonElement
    const loadingDiv = this.modal.querySelector('#auth-loading') as HTMLElement
    const form = this.modal.querySelector('#auth-form') as HTMLFormElement

    // Add null checks to prevent errors
    if (!submitBtn || !loadingDiv || !form) {
      console.warn('Auth modal elements not found during setLoading')
      return
    }

    if (loading) {
      submitBtn.disabled = true
      submitBtn.textContent = 'Processing...'
      loadingDiv.classList.remove('hidden')
      form.style.opacity = '0.6'
    } else {
      submitBtn.disabled = false
      submitBtn.textContent = this.isLoginMode ? 'Sign In' : 'Create Account'
      loadingDiv.classList.add('hidden')
      form.style.opacity = '1'
    }
  }

  private showEmailVerificationMessage(email: string, displayName: string): void {
    if (!this.modal) return

    // Replace the modal content with verification message
    this.modal.innerHTML = `
      <div class="modal-container modal-sm">
        <div class="modal-header">
          <h2 class="modal-title">Check Your Email</h2>
          <button id="auth-modal-close" class="modal-close-btn" aria-label="Close modal">&times;</button>
        </div>
        <div class="modal-body text-center">
          <div class="mb-6">
            <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Welcome, ${displayName}!</h3>
            <p class="text-gray-600 mb-4">
              We've sent a verification email to <strong>${email}</strong>
            </p>
            <p class="text-sm text-gray-500 mb-6">
              Please check your inbox and click the verification link to activate your account. 
              The link will expire in 24 hours.
            </p>
          </div>
          
          <div class="space-y-3">
            <button
              id="resend-verification"
              class="btn btn-secondary w-full"
            >
              Resend Verification Email
            </button>
            <button
              id="back-to-login"
              class="btn btn-outline w-full"
            >
              Back to Sign In
            </button>
          </div>
          
          <div class="mt-6 text-sm text-gray-500">
            <p>Didn't receive the email? Check your spam folder or try resending.</p>
          </div>
        </div>
      </div>
    `

    // Re-attach event listeners for the new content
    this.attachVerificationEventListeners(email)
  }

  private attachVerificationEventListeners(email: string): void {
    if (!this.modal) return

    // Close modal
    const closeBtn = this.modal.querySelector('#auth-modal-close')
    closeBtn?.addEventListener('click', () => this.hide())

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide()
      }
    })

    // Resend verification email
    const resendBtn = this.modal.querySelector('#resend-verification')
    resendBtn?.addEventListener('click', async () => {
      try {
        await authService.resendVerification(email)
        showToast('Verification email resent!', 'success')
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to resend verification email'
        showToast(errorMessage, 'error')
      }
    })

    // Back to login
    const backBtn = this.modal.querySelector('#back-to-login')
    backBtn?.addEventListener('click', () => {
      this.isLoginMode = true
      this.createModal()
      this.attachEventListeners()
    })
  }
}

// Export a function to show the auth modal
export function showAuthModal(isLoginMode: boolean = true, _onAuthSuccess?: (user: any) => void): void {
  // onAuthSuccess callback is no longer used since we reload the page
  const modal = new AuthModal()
  modal.show(isLoginMode)
}
